import { AppHexColor } from "~/__generated__/graphql"
import { cn } from "~/common/cn"
import { AppText } from "./app-text"
import circleWavyCheck from "~/images/circle-wavy-check"

const bgColorMap: Record<AppHexColor, string> = {
  [AppHexColor.Blue]: "bg-support-blue text-base-white",
  [AppHexColor.Clay]: "bg-support-clay text-base-black",
  [AppHexColor.Cream]: "bg-support-cream text-base-black",
  [AppHexColor.Green]: "bg-support-green text-base-black",
  [AppHexColor.Lime]: "bg-support-lime text-base-black",
  [AppHexColor.Magenta]: "bg-support-magenta text-base-black",
  [AppHexColor.Midnight]: "bg-support-midnight text-base-black",
  [AppHexColor.Moss]: "bg-support-moss text-base-black",
  [AppHexColor.Orange]: "bg-support-orange text-base-black",
  [AppHexColor.Pink]: "bg-support-pink text-base-black",
  [AppHexColor.Purple]: "bg-support-purple text-white",
  [AppHexColor.Red]: "bg-support-red text-base-black",
  [AppHexColor.Sky]: "bg-support-sky text-base-black",
  [AppHexColor.SoftLime]: "bg-support-soft-lime text-base-black",
  [AppHexColor.Yellow]: "bg-support-yellow text-base-black",
}

const keyFromString = (input: string): number => {
  let hash = 0

  for (let i = 0; i < input.length; i++) {
    hash = (hash << 5) - hash + input.charCodeAt(i)
    hash &= hash // Convert to 32bit integer
  }

  const min = 0
  const max = 14
  const range = max - min + 1
  const randomNumber = (((hash % range) + range) % range) + min

  return randomNumber
}

interface UserAvatarProps {
  avatarUrl?: string | null
  displayName: string
  size?: "xsm" | "sm" | "md" | "lg" | "shareCard"
  className?: string
  imgClassName?: string
  textClassName?: string
  verifiedAccount?: boolean
  verifiedBadgeSize?: "sm" | "md" | "lg"
}

const VerifiedBadge = ({ size }: { size: number }): JSX.Element => (
  <circleWavyCheck.Icon
    borderColor={"#FFFFFF"}
    borderWidth={1}
    fill="#2D92FF"
    width={size}
    height={size}
    className="absolute bottom-0 right-0"
  />
)

export const BadgeSize: Record<"sm" | "md" | "lg", number> = {
  sm: 13,
  md: 20,
  lg: 27,
}

export const UserAvatar = ({
  avatarUrl,
  displayName,
  verifiedAccount,
  size = "md",
  className,
  imgClassName,
  textClassName,
  verifiedBadgeSize = "sm",
}: UserAvatarProps): JSX.Element => {
  const sizeClasses = {
    xsm: "h-[20px] w-[20px]",
    sm: "h-[36px] w-[36px]",
    md: "h-[73px] w-[73px]",
    lg: "h-[99px] w-[99px]",
    shareCard: "w-[42px]",
  }

  const textSizeClasses = {
    xsm: "text-[10px]",
    sm: "text-[14px]",
    md: "text-[29px]",
    lg: "text-[40px]",
    shareCard: "text-[29px]",
  }

  if (avatarUrl) {
    return (
      <div className="relative flex-shrink-0">
        <img
          src={avatarUrl}
          alt={displayName}
          className={cn(
            sizeClasses[size],
            "aspect-square rounded-full border-[1px] object-cover",
            imgClassName
          )}
          loading="eager"
        />
        {verifiedAccount && <VerifiedBadge size={BadgeSize[verifiedBadgeSize]} />}
      </div>
    )
  }

  return (
    <div
      className={cn(
        "relative flex flex-row items-center justify-center gap-1 rounded-full border-[1px]",
        sizeClasses[size],
        "aspect-square rounded-full",
        bgColorMap[Object.values(AppHexColor)[keyFromString(displayName)]],
        className
      )}
    >
      <AppText
        variant="body3"
        className={cn(
          "font-md-nichrome font-bold opacity-70",
          textSizeClasses[size],
          textClassName
        )}
      >
        {displayName.charAt(0).toUpperCase()}
      </AppText>
      {verifiedAccount && <VerifiedBadge size={BadgeSize[verifiedBadgeSize]} />}
    </div>
  )
}
